import { createTheme, type Theme } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface TypeBackground {
    elevated?: string;
    subtle?: string;
  }
  // Soft tint for chips and callouts, e.g. bgcolor: 'success.lighter'
  interface PaletteColor {
    lighter?: string;
  }
  interface SimplePaletteColorOptions {
    lighter?: string;
  }
}

export type ThemeMode = 'dark' | 'light';

/**
 * PixelSpot brand tokens, per mode: the same "Quiet confidence" system the
 * marketing pages use (src/marketing/ds.css), so the product and the site
 * read as one brand.
 * Dark — the night world: black ground, raised #161617, tiles #1d1d1f.
 * Light — the day world: white ground, #f5f5f7 surfaces, #1d1d1f ink.
 * Colour is a signal, never decoration: blue is a booking or a link, green
 * is live or a proven play, violet is something a camera measured, amber
 * is waiting on someone, coral is a refusal.
 */
const DARK = {
  primary: '#3b6ef5',
  primaryLight: '#5c86ff',
  primaryDark: '#2a55d4',
  accentPurple: '#a78bfa',
  accentPurpleLight: '#c4b1ff',
  accentPurpleDark: '#7b5bf5',
  success: '#30d158',
  successDark: '#1f9d4d',
  warning: '#f5a524',
  warningDark: '#a86b06',
  error: '#ff6a4d',
  errorDark: '#b8321b',
  info: '#5c86ff',
  bg: '#000000',
  paper: '#161617',
  elevated: '#1d1d1f',
  subtle: '#111112',
  textPrimary: '#f5f5f7',
  textSecondary: '#a1a1a6',
  textTertiary: '#505054',
  textFaint: '#6e6e73',
  border: 'rgba(255,255,255,0.08)',
  borderMid: 'rgba(255,255,255,0.12)',
  borderStrong: 'rgba(255,255,255,0.2)',
  hover: 'rgba(255,255,255,0.04)',
  hoverStrong: 'rgba(255,255,255,0.06)',
  inputBg: 'rgba(255,255,255,0.03)',
  tableHeadBg: 'rgba(255,255,255,0.02)',
  chipBg: 'rgba(255,255,255,0.06)',
  appBarBg: 'rgba(0,0,0,0.82)',
  drawerBg: '#0a0a0b',
  scrollThumb: 'rgba(255,255,255,0.1)',
  scrollThumbHover: 'rgba(255,255,255,0.2)',
  backdrop: 'rgba(0,0,0,0.75)',
  skeleton: 'rgba(255,255,255,0.05)',
  progressTrack: 'rgba(255,255,255,0.06)',
  shadowSm: '0 1px 3px rgba(0,0,0,0.4), 0 1px 2px rgba(0,0,0,0.3)',
  shadowMd: '0 8px 24px rgba(0,0,0,0.5), 0 2px 8px rgba(0,0,0,0.4)',
  shadowLg: '0 24px 64px rgba(0,0,0,0.7), 0 8px 24px rgba(0,0,0,0.5)',
  shadowMenu: '0 8px 32px rgba(0,0,0,0.6)',
  glow: '0 0 0 1px rgba(59,110,245,0.3), 0 4px 16px rgba(59,110,245,0.15)',
  successSoft: 'rgba(48,209,88,0.12)',
  warningSoft: 'rgba(245,165,36,0.12)',
  errorSoft: 'rgba(255,106,77,0.12)',
  infoSoft: 'rgba(59,110,245,0.12)',
  alertSuccessBg: 'rgba(48,209,88,0.08)', alertSuccessBorder: 'rgba(48,209,88,0.25)', alertSuccessText: '#30d158',
  alertWarningBg: 'rgba(245,165,36,0.08)', alertWarningBorder: 'rgba(245,165,36,0.25)', alertWarningText: '#f5a524',
  alertErrorBg: 'rgba(255,106,77,0.08)', alertErrorBorder: 'rgba(255,106,77,0.25)', alertErrorText: '#ff6a4d',
  alertInfoBg: 'rgba(59,110,245,0.08)', alertInfoBorder: 'rgba(59,110,245,0.25)', alertInfoText: '#7aa2ff',
} as const;

const LIGHT: Record<keyof typeof DARK, string> = {
  primary: '#3b6ef5',
  primaryLight: '#5c86ff',
  primaryDark: '#2a55d4',
  accentPurple: '#7b5bf5',
  accentPurpleLight: '#a78bfa',
  accentPurpleDark: '#5b3fd6',
  success: '#1f9d4d',
  successDark: '#167a3b',
  warning: '#b7700a',
  warningDark: '#8a5407',
  error: '#e0392b',
  errorDark: '#b02a1f',
  info: '#3b6ef5',
  bg: '#ffffff',           // the day world: white ground
  paper: '#f5f5f7',        // raised surfaces
  elevated: '#ffffff',
  subtle: '#f5f5f7',
  textPrimary: '#1d1d1f',
  textSecondary: '#6e6e73',
  textTertiary: '#aeaeb2',
  textFaint: '#86868b',
  border: 'rgba(0,0,0,0.08)',
  borderMid: 'rgba(0,0,0,0.12)',
  borderStrong: 'rgba(0,0,0,0.2)',
  hover: 'rgba(0,0,0,0.04)',
  hoverStrong: 'rgba(0,0,0,0.06)',
  inputBg: 'rgba(0,0,0,0.02)',
  tableHeadBg: 'transparent',
  chipBg: 'rgba(0,0,0,0.06)',
  appBarBg: 'rgba(255,255,255,0.85)',
  drawerBg: '#f5f5f7',
  scrollThumb: 'rgba(23,24,28,0.15)',
  scrollThumbHover: 'rgba(23,24,28,0.28)',
  backdrop: 'rgba(23,24,28,0.35)',
  skeleton: 'rgba(23,24,28,0.06)',
  progressTrack: 'rgba(23,24,28,0.08)',
  shadowSm: '0 1px 2px rgba(23,24,28,0.05), 0 1px 3px rgba(23,24,28,0.04)',
  shadowMd: '0 8px 24px rgba(23,24,28,0.08), 0 2px 8px rgba(23,24,28,0.05)',
  shadowLg: '0 24px 64px rgba(23,24,28,0.12), 0 8px 24px rgba(23,24,28,0.07)',
  shadowMenu: '0 8px 32px rgba(23,24,28,0.12)',
  glow: '0 0 0 1px rgba(59,110,245,0.25), 0 4px 16px rgba(59,110,245,0.12)',
  successSoft: 'rgba(31,157,77,0.10)',
  warningSoft: 'rgba(183,112,10,0.10)',
  errorSoft: 'rgba(224,57,43,0.08)',
  infoSoft: 'rgba(59,110,245,0.10)',
  alertSuccessBg: 'rgba(31,157,77,0.08)', alertSuccessBorder: 'rgba(31,157,77,0.25)', alertSuccessText: '#167a3b',
  alertWarningBg: 'rgba(183,112,10,0.08)', alertWarningBorder: 'rgba(183,112,10,0.25)', alertWarningText: '#8a5407',
  alertErrorBg: 'rgba(224,57,43,0.06)', alertErrorBorder: 'rgba(224,57,43,0.22)', alertErrorText: '#b02a1f',
  alertInfoBg: 'rgba(59,110,245,0.07)', alertInfoBorder: 'rgba(59,110,245,0.22)', alertInfoText: '#2a55d4',
};

export const getModeTokens = (mode: ThemeMode): Record<keyof typeof DARK, string> =>
  mode === 'light' ? LIGHT : DARK;

/**
 * Backward-compatible token surface (dark values) — kept for existing imports.
 */
export const tokens = {
  color: {
    primary: DARK.primary,
    primaryLight: DARK.primaryLight,
    primaryDark: DARK.primaryDark,
    primaryAlpha08: 'rgba(59,110,245,0.08)',
    primaryAlpha12: 'rgba(59,110,245,0.12)',
    primaryAlpha16: 'rgba(59,110,245,0.16)',
    primaryAlpha24: 'rgba(59,110,245,0.24)',
    accentPurple: DARK.accentPurple,
    accentPurpleLight: DARK.accentPurpleLight,
    success: DARK.success,
    warning: DARK.warning,
    error: DARK.error,
    info: DARK.info,
    bg: DARK.bg,
    paper: DARK.paper,
    textPrimary: DARK.textPrimary,
    textSecondary: DARK.textSecondary,
    textTertiary: DARK.textTertiary,
    border: DARK.border,
    borderMid: DARK.borderMid,
    borderStrong: DARK.borderStrong,
  },
  radius: { sm: 6, md: 10, lg: 20, xl: 28, pill: 9999 },
  shadow: { sm: DARK.shadowSm, md: DARK.shadowMd, lg: DARK.shadowLg, glow: DARK.glow },
  // One family. Geist's lowercase l has a tail, so "PxlOS" never reads "PxIOS".
  font: {
    sans: "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    display: "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    mono: "'Geist Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
  },
} as const;

export const HERO_PATTERN = {
  p: { xs: 3, md: 4 },
  mb: 4,
  borderRadius: `${tokens.radius.lg}px`,
  background:
    'radial-gradient(900px 340px at 100% -8%, rgba(59,110,245,0.12), transparent 60%), #161617',
  border: `1px solid ${tokens.color.border}`,
  boxShadow: tokens.shadow.sm,
} as const;

export const PREMIUM_SURFACE = {
  backgroundColor: 'background.paper',
  border: `1px solid ${tokens.color.border}`,
  boxShadow: tokens.shadow.sm,
  borderRadius: `${tokens.radius.lg}px`,
} as const;

export function buildTheme(mode: ThemeMode): Theme {
  const c = getModeTokens(mode);
  return createTheme({
    spacing: 8,
    palette: {
      mode,
      primary: { main: c.primary, light: c.primaryLight, dark: c.primaryDark, lighter: c.infoSoft, contrastText: '#ffffff' },
      secondary: { main: c.accentPurple, light: c.accentPurpleLight, dark: c.accentPurpleDark, contrastText: '#ffffff' },
      success: { main: c.success, dark: c.successDark, light: c.successSoft, lighter: c.successSoft },
      warning: { main: c.warning, dark: c.warningDark, light: c.warningSoft, lighter: c.warningSoft },
      error: { main: c.error, dark: c.errorDark, light: c.errorSoft, lighter: c.errorSoft },
      info: { main: c.info, light: c.infoSoft, lighter: c.infoSoft },
      background: { default: c.bg, paper: c.paper, elevated: c.elevated, subtle: c.subtle },
      text: { primary: c.textPrimary, secondary: c.textSecondary, disabled: c.textTertiary },
      divider: c.border,
    },
    shape: { borderRadius: tokens.radius.md },
    // One family at a 1.2 scale from 14: 11 12 14 17 20 24 29 35 42. Sentence
    // case everywhere; headings carry the personality through size and tight
    // tracking, not through caps, eyebrows or a second face.
    typography: {
      fontFamily: tokens.font.sans,
      h1: { fontSize: '42px', fontWeight: 600, letterSpacing: '-0.03em', lineHeight: 1.05 },
      h2: { fontSize: '35px', fontWeight: 600, letterSpacing: '-0.025em', lineHeight: 1.08 },
      h3: { fontSize: '29px', fontWeight: 600, letterSpacing: '-0.02em', lineHeight: 1.12 },
      h4: { fontSize: '24px', fontWeight: 600, letterSpacing: '-0.015em', lineHeight: 1.2 },
      h5: { fontSize: '20px', fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.3 },
      h6: { fontSize: '17px', fontWeight: 600, letterSpacing: '-0.005em', lineHeight: 1.35 },
      subtitle1: { fontSize: '14px', fontWeight: 600, lineHeight: 1.4 },
      subtitle2: { fontSize: '12px', fontWeight: 600, lineHeight: 1.4 },
      body1: { fontSize: '14px', fontWeight: 400, lineHeight: 1.6, letterSpacing: '-0.005em' },
      body2: { fontSize: '13px', fontWeight: 400, lineHeight: 1.55, color: c.textSecondary },
      caption: { fontSize: '12px', fontWeight: 400, letterSpacing: '0', lineHeight: 1.4 },
      // Kept as a variant for the labels that use it, but it is now a quiet
      // sentence-case label, not a tracked-out mono eyebrow.
      overline: { fontSize: '12px', fontWeight: 500, letterSpacing: '0', textTransform: 'none', lineHeight: 1.4, color: c.textSecondary },
      button: { textTransform: 'none', fontWeight: 500, letterSpacing: '-0.005em' },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          // Theme surface as CSS variables so plain-string sx (hero panels
          // etc.) can reference the active mode without importing the theme.
          ':root': {
            '--ps-surface': c.paper,
            '--ps-border': c.border,
            '--ps-bg': c.bg,
            colorScheme: mode,
          },
          body: {
            // !important: index.html (the standalone landing page) sets its own
            // dark body background; without this the light theme renders dark
            // text on a black ground.
            backgroundColor: `${c.bg} !important`,
            color: c.textPrimary,
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
            scrollbarWidth: 'thin',
            scrollbarColor: `${c.scrollThumb} transparent`,
            transition: 'background-color 0.25s ease',
            '&::-webkit-scrollbar': { width: '4px', height: '4px' },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': { background: c.scrollThumb, borderRadius: '2px' },
            '&::-webkit-scrollbar-thumb:hover': { background: c.scrollThumbHover },
          },
          // Paint the app's own ground too — belt and braces against any
          // page-level CSS the landing document ships with.
          '#root': {
            backgroundColor: c.bg,
            minHeight: '100vh',
            transition: 'background-color 0.25s ease',
          },
          // Motion is kept for one moment per page (a screen powering on, a
          // sheet opening). Nothing moves on its own just to be noticed.
          '@media (prefers-reduced-motion: reduce)': {
            '*, *::before, *::after': { animationDuration: '0.001ms !important', transitionDuration: '0.001ms !important' },
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: c.appBarBg,
            backdropFilter: 'blur(20px)',
            borderBottom: `1px solid ${c.border}`,
            boxShadow: 'none',
            backgroundImage: 'none',
            color: c.textPrimary,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: c.drawerBg,
            borderRight: `1px solid ${c.border}`,
            width: '240px',
            boxShadow: 'none',
          },
        },
      },
      // Flat tiles, one radius for a tile (20) and one for a control (10).
      // No lift, no shimmer, no entrance: a tile is a surface, not a toy.
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: c.paper,
            border: `1px solid ${c.border}`,
            borderRadius: '20px',
            backgroundImage: 'none',
            boxShadow: 'none',
            position: 'relative',
            overflow: 'hidden',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: c.paper,
            backgroundImage: 'none',
            border: `1px solid ${c.border}`,
            borderRadius: '20px',
            boxShadow: 'none',
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: '9999px',
            fontWeight: 500,
            letterSpacing: '-0.005em',
            transition: 'background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease',
          },
          containedPrimary: {
            backgroundColor: c.primary,
            padding: '9px 22px',
            '&:hover': { backgroundColor: c.primaryLight },
          },
          outlined: {
            padding: '8px 22px',
            borderColor: c.borderMid,
            '&:hover': { borderColor: c.borderStrong },
          },
          text: {
            color: c.textSecondary,
            '&:hover': { color: c.textPrimary, backgroundColor: c.hover },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: '9999px',
            fontWeight: 500,
            fontSize: '12px',
            letterSpacing: '0',
            backgroundColor: c.chipBg,
            color: c.textSecondary,
          },
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: {
            backgroundColor: c.paper,
            borderRadius: '16px',
            border: `1px solid ${c.border}`,
            boxShadow: 'none',
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            '& .MuiTableCell-head': {
              backgroundColor: c.tableHeadBg,
              color: c.textSecondary,
              fontSize: '12px',
              fontWeight: 500,
              letterSpacing: '0',
              textTransform: 'none',
              borderBottom: `1px solid ${c.border}`,
              padding: '12px 20px',
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            color: c.textSecondary,
            fontSize: '13px',
            borderBottom: `1px solid ${c.border}`,
            padding: '14px 20px',
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            transition: 'background 0.15s ease',
            '&:hover': { backgroundColor: `${c.hover} !important` },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            backgroundColor: c.inputBg,
            borderRadius: '10px',
            '& .MuiOutlinedInput-notchedOutline': { borderColor: c.borderMid },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: c.borderStrong },
            '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: c.primary,
              boxShadow: '0 0 0 3px rgba(59,110,245,0.15)',
            },
            '& input': { color: c.textPrimary },
            '& label': { color: c.textFaint },
            '& label.Mui-focused': { color: c.primary },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: c.inputBg,
            borderRadius: '10px',
            '& .MuiOutlinedInput-notchedOutline': { borderColor: c.borderMid },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: c.borderStrong },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: c.primary,
              boxShadow: '0 0 0 3px rgba(59,110,245,0.15)',
            },
            '& input': { color: c.textPrimary },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: c.elevated,
            border: `1px solid ${c.borderMid}`,
            borderRadius: '20px',
            boxShadow: c.shadowLg,
          },
          container: {
            '& .MuiBackdrop-root': {
              backgroundColor: c.backdrop,
              backdropFilter: 'blur(8px)',
            },
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundColor: c.elevated,
            border: `1px solid ${c.borderMid}`,
            borderRadius: '12px',
            boxShadow: c.shadowMenu,
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            backgroundColor: c.elevated,
            border: `1px solid ${c.borderMid}`,
            borderRadius: '12px',
            boxShadow: c.shadowMenu,
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontSize: '13px',
            color: c.textSecondary,
            borderRadius: '6px',
            margin: '2px 6px',
            padding: '8px 12px',
            transition: 'all 0.2s cubic-bezier(0.2,0.8,0.2,1)',
            '&:hover': { backgroundColor: c.hoverStrong, color: c.textPrimary },
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: mode === 'dark' ? c.subtle : '#25262b',
            border: `1px solid ${mode === 'dark' ? c.borderMid : 'transparent'}`,
            color: '#f5f6f8',
            fontSize: '12px',
            borderRadius: '8px',
            padding: '6px 12px',
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: { backgroundColor: c.textPrimary, height: '2px', borderRadius: '2px 2px 0 0' },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '14px',
            color: c.textSecondary,
            minWidth: 'auto',
            padding: '10px 16px',
            transition: 'color 0.2s ease',
            '&.Mui-selected': { color: c.textPrimary },
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: { backgroundColor: c.progressTrack },
          bar: { backgroundColor: c.primary },
        },
      },
      MuiSkeleton: {
        styleOverrides: { root: { backgroundColor: c.skeleton } },
      },
      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: '12px', border: '1px solid' },
          filledSuccess: { backgroundColor: c.alertSuccessBg, borderColor: c.alertSuccessBorder, color: c.alertSuccessText },
          outlinedSuccess: { backgroundColor: c.alertSuccessBg, borderColor: c.alertSuccessBorder, color: c.alertSuccessText },
          standardSuccess: { backgroundColor: c.alertSuccessBg, borderColor: c.alertSuccessBorder, color: c.alertSuccessText },
          filledWarning: { backgroundColor: c.alertWarningBg, borderColor: c.alertWarningBorder, color: c.alertWarningText },
          outlinedWarning: { backgroundColor: c.alertWarningBg, borderColor: c.alertWarningBorder, color: c.alertWarningText },
          standardWarning: { backgroundColor: c.alertWarningBg, borderColor: c.alertWarningBorder, color: c.alertWarningText },
          filledError: { backgroundColor: c.alertErrorBg, borderColor: c.alertErrorBorder, color: c.alertErrorText },
          outlinedError: { backgroundColor: c.alertErrorBg, borderColor: c.alertErrorBorder, color: c.alertErrorText },
          standardError: { backgroundColor: c.alertErrorBg, borderColor: c.alertErrorBorder, color: c.alertErrorText },
          filledInfo: { backgroundColor: c.alertInfoBg, borderColor: c.alertInfoBorder, color: c.alertInfoText },
          outlinedInfo: { backgroundColor: c.alertInfoBg, borderColor: c.alertInfoBorder, color: c.alertInfoText },
          standardInfo: { backgroundColor: c.alertInfoBg, borderColor: c.alertInfoBorder, color: c.alertInfoText },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          switchBase: { color: c.textPrimary },
          track: {
            backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(23,24,28,0.2)',
            opacity: 1,
            '.Mui-checked + &': { backgroundColor: c.primary, opacity: 1 },
          },
        },
      },
      MuiDivider: {
        styleOverrides: { root: { borderColor: c.border } },
      },
      MuiBadge: {
        styleOverrides: { badge: { backgroundColor: c.primary, color: '#ffffff' } },
      },
    },
  });
}

const theme = buildTheme('dark');
export default theme;
